/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * MappingControl (#858 / #859-ph2) — the shared per-leaf mapping control.
 *
 * Given one {@link MappingLeaf}, lets the operator pick how it resolves:
 * **Static** value, **Macro** token, **Path** (context path), or a full
 * **Expression**. Path + Expression are backed by the same
 * {@link DecoratorExpressionEditor} used across rules, strategies, and pixels —
 * this is a thin wrapper, not a parallel mapping widget (guardrail from the
 * design spec). Reused by the ORTB body editor now; the VAST params editor and
 * pixel extraction editor (#527) can adopt it next.
 */

import React from 'react';
import type { EmptyValueOnSurface } from './empty-value-context';
import { Badge, Box, Button, Checkbox, HStack, Input, NativeSelect, Text, Textarea, VStack } from '@chakra-ui/react';
import { CONFIG_SECRET_PLACEHOLDER } from '@sensitive-headers';
import { MappingAutocompleteInput, type MappingSuggestion } from '../inputs/MappingAutocompleteInput';
import { DecoratorExpressionEditor, OutsideExpressionLocations } from './DecoratorExpressionEditor';
import {
  type CalculatedExpression,
  type MacroExpression,
  type ReferenceExpression,
  defaultCalculatedExpression,
} from '../../../types/decorator-expression';
import { useExpressionLibrary } from '../../../hooks/useExpressionLibrary';
import type { MappingLeaf, ScalarType } from './ortb-body-editor.adapters';
import { canonicalizeContextPath } from '@context-roots';
import { isRequiredHere, type DestinationFieldType, type FieldImportance } from '@destination-field';
import {
  coercibleScalar,
  EXPRESSION_YIELD_LABEL,
  EXPRESSION_YIELD_TYPES,
  isExpressionYieldType,
  sentType,
} from '@expression-yield';
import {
  describeOmissionAnswer,
  expressionDefaultOf,
  leafClaims,
  OMISSION_ANSWER_LABEL,
  withLeafDefault,
  type UnresolvedBehaviour,
} from './mapping-value-round-trip';
import {
  coerceStaticValue,
  isPlainObjectValue,
  isScalarArray,
  parseJsonValue,
  parseScalarArray,
  staticValueType,
} from './ortb-static-value';
import type { WireRendering, WireSurfaceClass } from '@wire-rendering';
import {
  ALWAYS_SENT,
  isDeclared,
  NEVER_SENT,
  omissionAnswerOf,
  omissionForAnswer,
  type MappingOmission,
  type OmissionAnswer,
} from '@wire-omission';
import { SendAsControl } from './SendAsControl';
import { CapabilityChipRow, openSlotForShape } from './CapabilityChipRow';
import { ValueLine, showsListOnLine } from './ValueLine';
import {
  chipCapabilitiesFor,
  compositionBasePath,
  isCarryable,
  rendersOnValueLine,
  reshapeExpression,
  wrapForErrand,
} from './value-line';
import { SharedExpressionSurfaces } from './SharedExpressionSurfaces';
import { useSharedExpressionSlot } from './useSharedExpressionSlot';
import { ExpressionLibraryAccessContext } from './expression-library-access';
import { describeUnusableHere, type ResolveSurfaces } from './shared-expression-usability';
import { walkExpression } from './expression-walk';
import type { CapabilitySlot } from './CapabilityChipRow';
import { MAPPING_HELP } from './mapping-help';
import { DefaultValueControl, ExpressionDefaultControl, KnobLabel, ReferenceDefaultControl } from './mapping-knobs';
import { ControlHover } from '../../ui/ControlHover';
import { FieldHelpTooltip } from '../../ui/FieldHelpTooltip';
import type { SavedLibraryEntry } from './DecoratorExpressionEditor';

/**
 * **What this surface can carry — declared, never defaulted into** (`N11g` · `#1177`,
 * `MAPPING_EDITOR_UX_GUIDANCE` pattern 24, ratified 2026-08-26).
 *
 * Measured before it was written: `MappingControl` had **2 required props and 13 optional**,
 * six of them per-surface capability declarations with silent defaults, and **no two surfaces
 * answered the same set**. `surface` is `#1083 · R1`'s own predicate — *"one predicate, named
 * once, rather than each site re-deciding"* — and one call site of four passed it. So R1
 * landed as a name rather than as a rule, and the shared widget looked shared while each
 * surface quietly got a different control.
 *
 * Every field here is **required and nullable** — `X | null`, never `X?` — which is `#1134`'s
 * treatment applied to capability rather than to callbacks. A surface with no such notion has
 * to say so; a surface that forgot cannot compile. Making the change produced an error at
 * every call site that had never answered, which is where the honest per-surface answers got
 * written down for the first time.
 *
 * It is one object rather than six props so that *a new capability* is one compile error per
 * surface, and so the pairs that only make sense together — a rendering and the handler that
 * stores it — cannot be half-answered.
 */
/**
 * **A capability this surface refuses, and what it tells the operator** (Todd, 2026-08-26:
 * *"mapping features that cannot be supported by the type should be disabled, not removed —
 * keeps the UI looking the same, and explicitly tells the user 'you can't do that here'"*).
 *
 * Removing a control makes the shared editor *look* different per surface, which is the thing
 * `#1177` is about seen from the operator's side: they learn the affordance on one surface,
 * do not find it on the next, and cannot tell whether it is missing, broken, or somewhere
 * else. A disabled control with a sentence is a **statement**; an absent one is a silence.
 *
 * The `reason` is operator-facing (#1045): it says what happens here, not what we did not
 * build, and where there is a way to get the same outcome it names it.
 *
 * `null` is still a legitimate third answer, and it is the narrower one: the capability has no
 * meaning on this surface at all, so a disabled control would invent a question. The test is
 * whether an operator who used it on another surface would come looking for it here.
 */
export interface CapabilityRefused {
  reason: string;
}

export function isRefused<T>(capability: T | CapabilityRefused | null): capability is CapabilityRefused {
  return !!capability && typeof capability === 'object' && 'reason' in capability;
}

export interface MappingSurfaceCapabilities {
  /**
   * **Is a `{macro}` a value this surface can read from** (`XS3` slice 3, `X27`).
   *
   * It was `tabs`, a list of the four tab ids — and once `X23` removed the strip, the only thing
   * that list still decided was this one question, which exactly one surface answered `no` to.
   * Inbound response mapping reads what the exchange sent; a `{macro}` is a value **bidBrain** has,
   * so it is not a source here.
   *
   * It reaches the classifier and not just the type-down, deliberately. `leafToSource` on that
   * surface has no macro branch, so a `{ip}` typed into the one value field would have been
   * accepted, stored, and discarded on Apply — the refusal going quiet at the moment the operator
   * was most likely to believe the row. Refused, it classifies as the literal it will be stored as
   * and the row says so.
   */
  acceptsMacro: true | CapabilityRefused;
  /**
   * **Whether this destination takes a value worked out per request** (a path, a macro, an
   * expression) **or only a fixed one** (`#1580`). Refused, the row is still an ordinary mapping row —
   * *to the user it's just another mapping* (Todd, 2026-10-06) — and the forms it cannot hold are
   * offered disabled with this reason (pattern 24) rather than missing: a round's floor is the case,
   * until a floor can be worked out when the round is planned. Required (`#1134`): every surface says.
   */
  acceptsDerived: true | CapabilityRefused;
  /**
   * **May a value here be picked from the shared expression library** (`X28a`, `XS3` slice 4).
   *
   * The `Shared…` chip is a **picker**, not a save button: at row level the useful act is *reach for
   * one that already exists*, and saving belongs in the editor where the authoring happened. `X28a`'s
   * argument for the direction is that *a library that is easy to draw from accumulates addresses,
   * while one that is easy to push into accumulates duplicates.*
   *
   * Refused on **response mapping**, and the reason is a real limit rather than unbuilt wiring: the
   * library's expressions read `session.*`, and that side reads what the exchange sent. Computing
   * *which* shared expressions a surface can actually resolve is `X18`, in `XS5`; until then the
   * surface where none of them work says so instead of offering a list that silently resolves to
   * nothing (`#1002` — a value reads as proof).
   *
   * `null` is for a surface with no library notion at all. Nothing answers that today, and it stays
   * available so the next surface can decline rather than be given a picker by default.
   */
  offersLibrary: true | CapabilityRefused | null;
  /**
   * **Which resolve context this surface's values are resolved against** (`X18`, `XS5`, `#1066`).
   *
   * A shared expression is reachable from many surfaces, so *the surface decides* has nobody to ask
   * — the surface declares what it offers, the expression declares what it reads, and usability is
   * the intersection. This is the first half, and it names a **context** rather than a list of roots
   * because `RESOLVE_CONTEXT_BUILDERS` already declares which roots each construction site sets and
   * `context-roots.spec.ts` checks that declaration against the real code. A per-surface root list
   * would be a second copy of a thing we already declare.
   *
   * `null` where a surface does not know which context it resolves against, and it is a real answer
   * rather than a gap: with no context named, **no claim is made** and nothing is marked. Guessing
   * would produce the one output this must never have — an expression marked as unable to resolve
   * where it resolves perfectly well.
   */
  resolvesAgainst: ResolveSurfaces | null;
  /**
   * **Can a row here store a scalar type** — the `$type` half of *Send as* (#923).
   *
   * `false` on parameters and headers is a **fix rather than a preference**: neither row has
   * anywhere to keep one, so every selection made there was discarded on Apply — *editable,
   * persists, read by nothing*, the #1017 family.
   */
  storesValueType: true | CapabilityRefused;
  /** What the outbound surface can carry. Decides whether `object` is offered. */
  surface: WireSurfaceClass;
  /**
   * **What this surface does with a derived value that resolves to nothing** and has no
   * default — the hint beside the Default control.
   *
   * `null` is *nobody has measured this surface*, and it is an honest answer: stating the
   * wrong one is the #1045 defect inside the control added to prevent one. The three that are
   * measured disagree, which is why it is per surface at all (CLAUDE.md's macro-resolution
   * table).
   */
  whenUnresolved: UnresolvedBehaviour | null;
  /**
   * **Send as** (D23 · R3) — the rendering for a value this surface cannot carry, and the
   * handler that stores it. `null` where the row has nowhere to keep one: `DemandSourceDto.headers`
   * is a flat name→value map, which is R1's side-car question rather than wiring.
   *
   * The value and its handler travel together so a surface cannot offer the control and drop
   * what the operator chooses — the shape the type select had on two surfaces before #1083.
   */
  sendAs:
    | {
        value: WireRendering | undefined;
        onChange: (next: WireRendering | undefined) => void;
        /**
         * **What the row sends** (`sentType`) — with `surface`, what `renderForWire` keys on (`4f`).
         * Required and nullable, so each surface says it; `null` is *nothing declares one*, which is
         * every text surface's honest answer.
         */
        sends: DestinationFieldType | null;
      }
    | CapabilityRefused
    | null;
  /**
   * **Can a *macro* row here carry a default** (`N11f`).
   *
   * A path or an expression keeps its default on the expression, which every surface stores, so
   * this asks only about a value that is *only* a macro. The ORTB body answers `false` and the reason is the
   * one thing that makes a declined capability legitimate: a body macro leaf is a bare string
   * in a document sent **verbatim**, so a default there needs a new document node — which is
   * `#1141`'s reframe (*a macro is sugar over a path*), not this slice's. Declining out loud is
   * the point; dropping it silently is what this whole declaration exists to stop.
   *
   * `null` is for a surface that accepts no macro at all — the question does not arise, and a
   * refusal about a value the surface cannot hold is a refusal about nothing.
   */
  storesMacroDefault: true | CapabilityRefused | null;
  /**
   * **Accept the system default** (`X33a`, `#1251`) — the third answer to the Default's own
   * question: *what happens when I do not supply a value.*
   *
   * A literal says *send this instead*, blank says *send nothing*, and this says **the system's own
   * value stands**. It is not a claim about whether the row goes out, which is why it sits beside
   * the Default rather than joining the omission select — two claims on one control is `X46`.
   *
   * **Three answers, and they are genuinely different claims:**
   *
   * | | Means |
   * | --- | --- |
   * | the object | there is a value beneath this row, and here is its state |
   * | `CapabilityRefused` | there **is** one and this surface cannot offer the control — with why |
   * | `null` | the question does not arise: nothing is beneath a row here |
   *
   * The middle one exists because collapsing it into `null` would be a **false claim** rather than a
   * missing feature — a header row *does* have protocol defaults beneath it, and saying nothing is
   * beneath it would be silence about a value the driver really sends.
   *
   * Whether one exists is the **surface's** question, answered from its own declaration
   * (`AUTO_POPULATED_VAST_PARAMS` · `ORTB_MERGE_BASE` · `DRIVER_SUPPLIED_TARGETS`) — this control
   * never guesses.
   *
   * `from` names **where the value comes from** in the operator's terms, so the control states the
   * value rather than the mechanism (`#1045`). The inner `refused` is the narrower case: the state
   * can be shown and a change cannot be stored, so the box renders **ticked and disabled with its
   * reason** rather than removed — a ticked box nobody can untick is a statement, and one that
   * silently discards the untick is `#1017`.
   */
  acceptsSystemDefault:
    | { from: string; value: boolean; onChange: (next: boolean) => void; refused?: CapabilityRefused | null }
    | CapabilityRefused
    | null;
  /**
   * **What an authored empty value does on this surface** — sent as `name=`, or left off with the
   * driver's own value standing (`empty-value-context.ts` holds the measurement). Required, because
   * a surface that forgot would show a branch's empty *Send this value* with nothing said about it,
   * which is how `cid=` reached the wire in `S3b`'s rehearsal. `null` where it cannot be stated.
   */
  emptyValue: EmptyValueOnSurface['outcome'] | null;
  /**
   * **Is this on the wire, and if not, when not** (`P1` slice 3).
   *
   * One outcome under a condition, replacing `suppress` and `optional` as separate concepts
   * (`MAPPING_EDITOR_UX_GUIDANCE` pattern 24, ratified by Todd 2026-08-27). Only the two
   * **unconditional** conditions have controls — *always* and *when it did not resolve* — because
   * only those are unconditional to author. Everything else states a condition, and a condition is
   * a mapping.
   *
   * It lives here rather than in each editor because it *is* part of the mapping: the parameter
   * surface rendered its own pair of checkboxes outside this control, which is how a shared editor
   * ends up not being shared. What differs per surface is only whether the wire can express the
   * answers, and that is what a surface declares.
   */
  /**
   * **How much this destination wants a value here** (`X46`, 2026-09-02) — and it is deliberately
   * **not** part of {@link MappingControlProps.declaresOmission}.
   *
   * Raised by Todd against `X29`: *"omission and optional are different concepts and shouldn't be
   * conflated."* They were, on one boolean. The separating test is **does changing this alter what
   * goes on the wire** — omission does; this never does. It decides what the badge says, what the
   * Prove phase asks for, and nothing else.
   *
   * `null` is for a surface where the question does not arise — a request header, which has no
   * destination declaration to be required by.
   */
  declaresImportance:
    | {
        /** The claim in force, or `undefined` where nobody has made one — which is not *optional*. */
        value: FieldImportance | undefined;
        onChange: (next: FieldImportance | undefined) => void;
      }
    | CapabilityRefused
    | null;
  /**
   * **Whether this row's fixed value is a credential** (`AUTHORED_CREDENTIALS_AT_REST_DESIGN.md` `CR2`,
   * `#1371`). Declared on the row, never matched by name; a surface seeds it from `suggestsSecretValue`
   * on a row created this visit, and the operator may untick it.
   *
   * `null` where the surface stores no credential (a response mapping reads values, it does not author
   * them). Refused where a credential could be typed and would not be protected, with the reason.
   */
  declaresSecret:
    | {
        value: boolean;
        onChange: (next: boolean) => void;
        /**
         * The deployment's answer (`runtimeSecretsEncrypted`). Only `false` adds a sentence — the case an
         * operator ticking *Secret* would not assume — and `undefined` claims nothing (`#1363`, `CR7`).
         */
        encryptedAtRest: boolean | undefined;
      }
    | CapabilityRefused
    | null;
  declaresOmission:
    | {
        value: MappingOmission;
        onChange: (next: MappingOmission) => void;
        /**
         * **A surface may express one condition and not the other**, so each is refused on its own
         * (added 2026-08-27, correcting a whole-capability refusal that took the wrong thing away).
         *
         * Headers are the case that forced it: *may be absent* decides nothing there — a header
         * that resolves to nothing is already omitted unconditionally (#998) — while *never send*
         * is the **only** way to stop `X-Forwarded-For`, which the driver adds on the operator's
         * behalf and no row could previously remove. Refusing the pair wholesale refused the one
         * that mattered.
         */
        refuses?: { optional?: CapabilityRefused; neverSend?: CapabilityRefused };
        /**
         * **This surface layers over another, so declining is a fourth answer** (`X33`, `XS3`
         * slice 5).
         *
         * A round-plan override sits on top of a demand source's own row, so *fall back to the
         * source's value* is a thing the operator can mean — and `X33`'s point is that it is
         * **the absence of an override**, not a new mechanism: naming it is what lets them undo a
         * per-round decision without deleting the row and losing the note on it.
         *
         * `null` is a surface with nothing beneath it, and it is the fourth answer's whole guard:
         * offering *fall back* where there is nothing to fall back to would name a mechanism that
         * does not exist there (#1045). The four demand-source surfaces all answer `null` — a
         * parented artifact's *Revert to parent* is a different gesture on a different axis
         * (`X31`, slice 7), which reverts the **stored** row rather than declining to state one.
         *
         * **`beneath` is a noun phrase, not a label**, so the option and its sentence cannot come
         * to disagree: the control composes both from it.
         */
        layering: { beneath: string; onInherit: () => void; inheriting: boolean } | null;
      }
    | CapabilityRefused
    | null;
}

/**
 * **Every prop here is required. Nullable is how a surface declines one.**
 *
 * Not a style preference — it is `#1134`'s rule applied to the whole control instead of one prop
 * at a time, after the sixth instance in two days. An optional prop through a shared component
 * compiles, type-checks, passes every spec and **renders nothing**, and the surface that forgot it
 * looks identical to the surface that decided against it. `onSaveToLibrary?:` is the worked
 * example: it was absent from the ORTB body editor — the surface an operator is most likely to
 * build a reusable expression on — and nothing anywhere could say so.
 *
 * `N11g` measured this control at *2 required props and 13 optional, six of them per-surface
 * capability declarations, and no two surfaces answering the same set*. `can` closed the six.
 * These are the rest, and there is deliberately **no category to judge**: a prop is required, and
 * `null` is a surface stating that it declines. Adding one is a compile error at every call site
 * until each has answered, which is the audit rather than a promise to do one.
 */
export interface MappingControlProps {
  leaf: MappingLeaf;
  onChange: (leaf: MappingLeaf) => void;
  /** What this surface can carry — see {@link MappingSurfaceCapabilities}. */
  can: MappingSurfaceCapabilities;
  /** Promote this mapping into the expression library; `null` where the surface offers no library. */
  onSaveToLibrary: ((name: string, description: string, expr: MacroExpression) => Promise<SavedLibraryEntry>) | null;
  /**
   * Autocomplete source for the Path tab. `null` uses the outbound bidBrain context schema;
   * inbound editors pass the response-domain field catalog.
   */
  pathSuggestions: ((query: string) => MappingSuggestion[] | Promise<MappingSuggestion[]>) | null;
  /** Placeholder for the Path input (domain-specific example); `null` for the generic one. */
  pathPlaceholder: string | null;
  /**
   * The kind of value the protocol declares for this leaf, when the caller knows it (the ORTB
   * body editor passes `catalogValueKindForPath`). A scalar is the default **Send as** for a static
   * value, so a numeric field does not need to be told it is numeric — see
   * `ScalarStaticValueEditor`. `'array'` is what lets a field holding one value become the list it
   * takes (`#1130`) — see `ListConversionNotice`. `null` where the surface has no catalog.
   */
  declaredType: DestinationFieldType | null;
  /**
   * Common values for this field, offered as you type on the **Static** tab. Header
   * names have a small, knowable value set (`X-Forwarded-For` is essentially always
   * `{ip}`), and typing it from memory is where a subtly wrong value comes from.
   * `null` where the surface has none to offer.
   */
  staticSuggestions: string[] | null;
}

/**
 * What a surface does with a derived value that resolved to nothing and has no default.
 *
 * **Declared in `mapping-value-round-trip.ts` and re-exported here**, because it is a property of
 * the *mapping*, not of the control: the required mark reads it too, to say what an unfilled
 * required row actually does — `foo=` on a VAST parameter, an omitted key in an ORTB body. A
 * second copy would let those two sentences disagree.
 */
export type { UnresolvedBehaviour };

/**
 * **`UNRESOLVED_HINT` was deleted here, not moved** (#1179, `XS3` slice 3, 2026-09-03).
 *
 * It said *"Leave Default blank to send nothing when it does not resolve"* — one of three
 * surface-specific spellings, appended to the tab's own hint, and named `Default` by name after the
 * 2026-08-27 report that it did not. Todd, opening this slice: *"the help text is confusing vs the
 * options in the 'on the wire' drop down."*
 *
 * It was. Since `X29` the omission select answers the same question **per answer and per surface**
 * ({@link describeOmissionAnswer} over the same {@link UnresolvedBehaviour}), so the row carried two
 * sentences about one event and the weaker one was the more prominent. This is #1179's third
 * category — *prose standing in for a control that now exists* — and moving it into the hover would
 * have preserved the contradiction where it is even less likely to be corrected.
 *
 * What replaces it is a **deferral**: `MAPPING_HELP.default` says what the box does and hands the
 * *what happens when nothing resolves* question to the control that owns it. That is the same shape
 * as `N11f`'s deferral to `optional` on the parameter surface — one answer per question, on the
 * control that decides it.
 */

const SCALAR_TYPES: ReadonlyArray<ScalarType> = ['string', 'number', 'boolean'];

/**
 * **`isSimplePath` decides how the row renders, never where the row goes** (`X23`).
 *
 * It used to route: a `CalculatedExpression` that grew a regex or a transform was sent to the
 * Expression tab, which is `F3` — one model split across two tabs, with nothing on the Path tab
 * saying so. Then slice 1 stopped it routing to the Expression tab, and slice 3 removed the strip
 * altogether, so there is nowhere left to route *to*.
 *
 * The predicate now lives in `value-line.ts` as `rendersOnValueLine`, with the name it earned: it is
 * not about paths, it is about whether the value fits in one text field. Where it does not — a
 * structured static, a conditional, a computed expression — the value line reads back a summary and
 * the editor for that shape opens beneath it, in the row's own expansion (`X40`).
 */

/**
 * Structured (object / complex array) static value, edited as JSON — the same
 * text the Raw JSON view holds for that field. Backed by **local text state** so
 * a half-typed value (`{"foo":` …) isn't parsed out from under the operator;
 * the leaf is only updated once the text parses. Keys typed here become their
 * own fields when the row is closed, exactly as if typed in Raw JSON.
 */
const StructuredValueEditor: React.FC<{ leaf: MappingLeaf; onChange: (leaf: MappingLeaf) => void }> = ({
  leaf,
  onChange,
}) => {
  const serialized = React.useMemo(() => JSON.stringify(leaf.staticValue ?? {}, null, 2), [leaf.staticValue]);
  const [text, setText] = React.useState(serialized);
  const [error, setError] = React.useState<string | null>(null);

  // Resync only on an external change (row switch, Fix, paste) — never while the
  // current text still represents the stored value, which would fight typing.
  React.useEffect(() => {
    const parsed = parseJsonValue(text);
    if (parsed.error || JSON.stringify(parsed.value) !== JSON.stringify(leaf.staticValue ?? {})) {
      setText(serialized);
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serialized]);

  const onText = (next: string) => {
    setText(next);
    const parsed = parseJsonValue(next);
    if (parsed.error) {
      setError(parsed.error);
      return;
    }
    setError(null);
    onChange({ path: leaf.path, kind: 'static', staticValue: parsed.value });
  };

  return (
    <VStack align="stretch" gap={1}>
      <Textarea
        size="sm"
        bg="bg.card"
        fontFamily="mono"
        fontSize="xs"
        rows={Math.min(10, Math.max(3, text.split('\n').length))}
        value={text}
        onChange={(e) => onText(e.target.value)}
        placeholder={'{\n  "asi": "{domain}"\n}'}
      />
      {error ? (
        <Text fontSize="xs" color="error.solid">
          Invalid JSON — {error}
        </Text>
      ) : (
        // The consequence only: *JSON* is what the box visibly holds, and *each mappable* is what a field
        // row already is (#1179, deleted rather than moved).
        <Text fontSize="xs" color="fg.muted">
          Object keys become their own fields when you close this row.
        </Text>
      )}
    </VStack>
  );
};

/**
 * A scalar list, edited as the comma-separated text the hint promises.
 *
 * **Backed by local text state**, for the third time in this file and the same reason
 * (`ScalarStaticValueEditor`, `MacroTokenEditor`): rendering the input from the *parsed*
 * value makes the separator undeleteable and unenterable. `parseScalarArray` drops empty
 * entries, so `video/mp4,` parses to `['video/mp4']`, which re-renders as `video/mp4` —
 * the comma is removed as fast as it is typed, and a list of one can never become a list
 * of two. Reported against `imp[].video.mimes`, where the hint says *comma-separated* and
 * the field refuses a comma.
 *
 * Resync only when the draft no longer *parses to* what is stored — a leaf switch or an
 * edit from elsewhere. Comparing against `value.join(', ')` instead would fight typing in
 * exactly the way it does today.
 */
const ScalarListStaticEditor: React.FC<{
  leaf: MappingLeaf;
  onChange: (leaf: MappingLeaf) => void;
}> = ({ leaf, onChange }) => {
  const stored = (leaf.staticValue ?? []) as Array<string | number | boolean | null>;
  const [text, setText] = React.useState(() => stored.join(', '));

  const same = (a: unknown[], b: unknown[]) => a.length === b.length && a.every((v, i) => v === b[i]);

  React.useEffect(() => {
    if (!same(parseScalarArray(text), stored)) setText(stored.join(', '));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leaf.staticValue]);

  return (
    <VStack align="stretch" gap={1}>
      <Input
        size="sm"
        bg="bg.card"
        fontFamily="mono"
        value={text}
        placeholder="Comma-separated list — e.g. video/mp4, video/webm"
        onChange={(e) => {
          setText(e.target.value);
          onChange({ path: leaf.path, kind: 'static', staticValue: parseScalarArray(e.target.value) });
        }}
      />
      {/* *Comma-separated* is the placeholder's word already; the numbers are what it cannot say (#1179). */}
      <Text fontSize="xs" color="fg.muted">
        Numeric entries (e.g. protocols) are sent as numbers.
      </Text>
    </VStack>
  );
};

/**
 * **A field that takes a list, holding one value** (`#1130`).
 *
 * The catalog declares `video.api` a list and the list editor could author one, but nothing led from
 * a stored scalar into it: `1,2,7` typed as a value, pasted, or written before the field was a list
 * stays the string `"1,2,7"` — one value on the wire where the exchange reads a list. Validation
 * already says so (`checkDestinationValue`); this is the fix, on the row that has the problem.
 *
 * **It converts only when asked**, never on read: the value at rest is what the operator stored, and
 * re-typing it underneath them is the stored-derivation mistake. The split is the list editor's own
 * (`parseScalarArray`), so numbers become numbers exactly as they do when typed there.
 */
const ListConversionNotice: React.FC<{
  leaf: MappingLeaf;
  onChange: (leaf: MappingLeaf) => void;
}> = ({ leaf, onChange }) => {
  const value = leaf.staticValue;
  if (leaf.kind !== 'static' || Array.isArray(value) || isPlainObjectValue(value)) return null;
  if (value === undefined || value === null || value === '') return null;
  const text = String(value);
  return (
    <HStack gap={2} align="center" flexWrap="wrap">
      <Text fontSize="xs" color="warning.ink">
        This field takes a list.{' '}
        <Text as="span" fontFamily="mono">
          {text}
        </Text>{' '}
        goes out as one value.
      </Text>
      <Button
        size="xs"
        variant="outline"
        borderColor="border.default"
        onClick={() => onChange({ path: leaf.path, kind: 'static', staticValue: parseScalarArray(text) })}
      >
        Split it into a list at the commas
      </Button>
    </HStack>
  );
};

/**
 * Static-value editor. Scalars edit inline; scalar arrays (and empty arrays)
 * edit as a comma-separated list; objects and complex arrays edit as JSON.
 */
const StaticValueEditor: React.FC<{
  leaf: MappingLeaf;
  onChange: (leaf: MappingLeaf) => void;
  declaredType: DestinationFieldType | null;
  suggestions: string[] | null;
  storesValueType: boolean;
}> = ({ leaf, onChange, declaredType, suggestions, storesValueType }) => {
  const value = leaf.staticValue;

  if (Array.isArray(value) && isScalarArray(value)) {
    return <ScalarListStaticEditor leaf={leaf} onChange={onChange} />;
  }

  if (Array.isArray(value) || isPlainObjectValue(value)) {
    return <StructuredValueEditor leaf={leaf} onChange={onChange} />;
  }

  return (
    <ScalarStaticValueEditor
      leaf={leaf}
      onChange={onChange}
      declaredType={declaredType}
      suggestions={suggestions}
      storesValueType={storesValueType}
    />
  );
};

/**
 * A scalar static value, with its own **Send as** (#923).
 *
 * The other three tabs had this and the static tab did not, so a `bidfloor` of `2.5`
 * or an `ext.number_of_ads` of `6` shipped as `"2.5"` / `"6"` — a type violation on a
 * numeric OpenRTB field, and one the operator could not correct from here.
 *
 * No `$valueType` reaches the wire: the ORTB body is JSON, so the coercion happens at
 * authoring time and the stored body is literally correct. The select therefore shows
 * the type the value *currently is*, read back from the stored JSON.
 *
 * Backed by local text state for the same reason `MacroTokenEditor` is: coercing on
 * every keystroke and re-deriving the input from the stored value makes `2.` collapse
 * to `2` as fast as the decimal point is typed. We resync only when the stored value
 * changes to something the current text does not already represent.
 */
const ScalarStaticValueEditor: React.FC<{
  leaf: MappingLeaf;
  onChange: (leaf: MappingLeaf) => void;
  /** The kind OpenRTB declares for this path; `null` where the catalog does not know it. */
  declaredType: DestinationFieldType | null;
  /** Common values for this field, shown as you type; `null` where there are none. */
  suggestions: string[] | null;
  /** Whether this row stores a scalar type at all — see `MappingControl`. */
  storesValueType: boolean;
}> = ({ leaf, onChange, declaredType, suggestions, storesValueType }) => {
  const stored = leaf.staticValue;
  const [text, setText] = React.useState(() => (stored == null ? '' : String(stored)));
  // What the value already IS wins; otherwise what the spec says this field is. A
  // new numeric field is seeded as `''` (`EMPTY_BY_TYPE.number`), so without the
  // catalog fallback typing `0` into `bidfloor` produced the string `"0"` — while
  // the same `0` in Raw JSON was a number. The operator should not have to tell us
  // that `bidfloor` is numeric.
  // A declared **list** says nothing about a scalar's type — that case is `ListConversionNotice`'s —
  // and neither does an object.
  const declaredScalar = coercibleScalar(declaredType ?? undefined) ?? null;
  const asType = staticValueType(stored === '' ? undefined : stored) ?? declaredScalar ?? 'string';

  // Resync only when the current text no longer *coerces to* what is stored — an
  // external edit or a leaf switch. Comparing `String(stored)` to the text instead
  // would fight typing: `2.` coerces to `2`, whose rendering is `'2'`, so the effect
  // would delete the decimal point as fast as it was typed (the bug `MacroTokenEditor`
  // documents for braces).
  React.useEffect(() => {
    if (coerceStaticValue(text, asType) !== stored) setText(stored == null ? '' : String(stored));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stored]);

  const emit = (raw: string, type: 'string' | 'number' | 'boolean' | undefined) =>
    onChange({ path: leaf.path, kind: 'static', staticValue: coerceStaticValue(raw, type) });

  return (
    <VStack align="stretch" gap={2}>
      {suggestions && suggestions.length > 0 ? (
        <MappingAutocompleteInput
          value={text}
          onChange={(next) => {
            setText(next);
            emit(next, asType);
          }}
          placeholder="Fixed value sent as-is"
          size="sm"
          bg="bg.card"
          minChars={0}
          suggestions={suggestions.map((v) => ({ value: v, category: 'common' }))}
        />
      ) : (
        <Input
          size="sm"
          bg="bg.card"
          fontFamily="mono"
          value={text}
          placeholder="Fixed value sent as-is"
          onChange={(e) => {
            setText(e.target.value);
            emit(e.target.value, asType);
          }}
        />
      )}
      {storesValueType && (
        <HStack gap={2}>
          <Text fontSize="xs" color="fg.muted">
            Send as
          </Text>
          <NativeSelect.Root size="xs" width="auto">
            <NativeSelect.Field
              value={asType}
              onChange={(e) => emit(text, e.target.value as 'string' | 'number' | 'boolean')}
              aria-label="Static value type"
              bg="bg.card"
              color="fg.default"
              fontFamily="mono"
            >
              {SCALAR_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </NativeSelect.Field>
            <NativeSelect.Indicator />
          </NativeSelect.Root>
          {asType === 'number' && typeof stored === 'string' && stored.trim() !== '' && (
            <Text fontSize="xs" color="warning.ink">
              not a number yet — will send as text
            </Text>
          )}
        </HStack>
      )}
    </VStack>
  );
};

const isStructuredStatic = (value: unknown) => Array.isArray(value) || isPlainObjectValue(value);

/**
 * The Path tab's edit — **keep everything the calculated expression already carried**.
 *
 * The return type is annotated deliberately: a literal containing a spread is exempt from
 * excess-property checking, and an *inferred* return type makes the value non-fresh at
 * every call site, so a stale key would type-check through both holes (CLAUDE.md, *a
 * rename is not finished when `tsc` goes green*).
 */
export const withPath = (expr: MacroExpression | undefined, path: string): CalculatedExpression => {
  const prev = expr?.type === 'calculated' ? expr : undefined;
  // **Canonicalise at the model boundary, not at each caller** (`#1213`). An alias is an *input
  // spelling* — `user.deviceType` is what a person types, `session.user.deviceType` is what
  // resolves — so it is rewritten here, where every value-line edit passes, rather than at the two
  // call sites plus whichever the next surface adds.
  //
  // **One root vocabulary serves every surface**, inbound included: `response` is a declared context
  // root since `#1213`, so an extraction path canonicalises through the same table an outbound one
  // does. Measured while rehearsing this slice, after a per-surface root list was built on the
  // assumption it did not — the assertion is in `MappingControl.capabilities.test.tsx`.
  return { ...prev, type: 'calculated', path: canonicalizeContextPath(path) };
};

/**
 * **The Default on the value line, for whichever kind the value currently is** (`XS3` slice 2).
 *
 * It used to be path-only, because the macro's Default lived on the Macro tab. Once a macro lands on
 * the value line (`X27` — a macro is sugar over a path) that tab is no longer where its default is
 * authored, and **its refusal has to travel with it**: `storesMacroDefault` is refused on a surface
 * whose macro leaf is a bare string in a document sent verbatim, and pattern 24 says a refused
 * capability is *disabled with its sentence*, never removed. Dropping the refusal here would have
 * turned a stated limit into a silently missing control, which is the silence pattern 24 exists to
 * end — and it is what the capabilities spec caught when the routing changed.
 */
const LeafDefaultControl: React.FC<{
  leaf: MappingLeaf;
  onChange: (next: MappingLeaf) => void;
  storesMacroDefault: true | CapabilityRefused | null;
  /** `X33a` — the third answer to the Default's question, where this row has a value beneath it. */
  acceptsSystemDefault: MappingSurfaceCapabilities['acceptsSystemDefault'];
}> = ({ leaf, onChange, storesMacroDefault, acceptsSystemDefault }) => {
  if (leaf.expression?.type === 'macro') {
    return (
      <DefaultValueControl
        value={expressionDefaultOf(leaf.expression, null)}
        onChange={(next) => onChange(withLeafDefault(leaf, next))}
        refused={isRefused(storesMacroDefault) ? storesMacroDefault : null}
        systemDefault={acceptsSystemDefault}
      />
    );
  }
  /**
   * **Every expression shape that declares a default gets the control** (`XS3` slice 4), and
   * *which shapes those are* is {@link ExpressionDefaultControl}'s answer rather than this
   * component's (`X38`). The editor renders the same control on a branch and on its own value line,
   * so a second copy of the rule here is what let two boxes stand over one slot.
   */
  const expr = leaf.expression;
  if (!expr) return null;
  /**
   * **A shared expression's default has a layer beneath it** (`X36`, `XS5`, `#1066`), so the row's
   * own is an **override** rather than the only copy. It is a different control from the one below
   * because it answers a different question — *replace what this expression already falls back to*
   * — and `X36` names `X31`'s inheritance vocabulary for it rather than the checkbox.
   */
  if (expr.type === 'reference') {
    return <ReferenceDefaultControl expression={expr} onChange={(next) => onChange({ ...leaf, expression: next })} />;
  }
  return (
    <ExpressionDefaultControl
      expression={expr}
      onChange={(next) => onChange({ ...leaf, expression: next })}
      systemDefault={acceptsSystemDefault}
    />
  );
};

/**
 * **Type** — the scalar type a resolved value is coerced to (#923).
 *
 * **It was *Send as* until 2026-09-04** (`#1246`, Todd: *"I think just `Type` is sufficient"*), and
 * the old label was **false on half its surfaces**: response mapping stores `MappingEntryDto.asType`
 * and sends nothing — the coercion is what turns `"12.50"` into a number before it is written onto
 * the ad. The control belongs there; the verb did not.
 *
 * One direction-neutral word rather than a word per surface. A per-surface label would be `X3`
 * arriving again — one concept under two names — and this coercion is genuinely the same question on
 * both sides: *what type is this value*.
 *
 * `SendAsControl` keeps its own name legitimately: it decides how a structure rides a **wire**, and
 * it is `null` on the surfaces that have none.
 *
 * Rendered **disabled with its reason** where the surface cannot store one, rather than
 * removed (Todd, 2026-08-26). It used to vanish on the two surfaces that cannot keep it,
 * which is silence about a control the operator has already met on the other two — and before
 * that it rendered *and discarded every choice*, which is the same silence with a worse
 * outcome (#1017's family).
 */
const ValueTypeControl: React.FC<{
  leaf: MappingLeaf;
  onChange: (next: string) => void;
  capability: true | CapabilityRefused | null;
  /**
   * The slot's declared type, which `auto` sends where no `Type` is set (`FD13`). `null` where the
   * surface's runtime does not convert to one, so `auto` claims nothing it does not do.
   */
  destination: DestinationFieldType | null;
}> = ({ leaf, onChange, capability, destination }) => {
  const expression = leaf.kind === 'expression' ? leaf.expression : undefined;
  // **A literal goes as the field's type, like any value** (`FD13`, Todd 2026-09-25), and as its own
  // where the field declares none. It has no expression, so `sentType` cannot see what it yields.
  if (leaf.kind === 'static') {
    const own = staticValueType(leaf.staticValue);
    const beneath: ReturnType<typeof sentType> = destination
      ? { type: destination, source: 'destination', yields: own, converts: undefined }
      : own
        ? { type: own, source: 'derived', yields: own, converts: 'always' }
        : undefined;
    return (
      <ValueTypeSelect value={leaf.asType} beneath={beneath} from={null} onChange={onChange} capability={capability} />
    );
  }
  // **Only a row that addresses the library reads it** — `ReferenceDefaultControl`'s rule, and the
  // reason this is a branch rather than one hook call: a row with no reference has no question for
  // the library to answer, and every surface that mounts this control would otherwise have to.
  if (expression?.type === 'reference') {
    return (
      <ReferencedValueTypeControl
        leaf={leaf}
        reference={expression}
        onChange={onChange}
        capability={capability}
        destination={destination}
      />
    );
  }
  return (
    <ValueTypeSelect
      value={leaf.asType}
      beneath={sentType(undefined, expression, destination, null)}
      from={null}
      onChange={onChange}
      capability={capability}
    />
  );
};

/**
 * **`X18` after placement** — a row holding a reference re-asks, against its own surface, the question
 * the picker asked when the expression was chosen (slice `4e`).
 *
 * `XS5` built `X18` at **pick time**: `SharedExpressionPicker` marks an expression that reads a root
 * this surface does not offer. Nothing asked again once the address was on the row, so an expression
 * edited in the library to read `params.pubid` made every response-mapping row using it resolve to
 * nothing, silently. This is the same sentence (`describeUnusableHere` — `#1187`, one decision, one
 * sentence), asked where the row is rather than where it was chosen.
 *
 * It lives here because `MappingControl` is the one component every surface's rows render through,
 * so all of them answer at once — the per-surface version is the thing the overriding rule is about.
 * A negative claim only (`E1`): the absence of this line means nothing was found against it, not that
 * the expression works. The library-editor half — *this edit breaks N rows* — is `#1417`.
 */
const PlacedReferenceUsability: React.FC<{
  expression: MacroExpression | undefined;
  resolvesAgainst: ResolveSurfaces | null;
}> = ({ expression, resolvesAgainst }) => {
  const refs: string[] = [];
  walkExpression(expression, {
    value: (node) => {
      if (node.type === 'reference' && !refs.includes(node.ref)) refs.push(node.ref);
    },
  });
  // No reference, or a surface that names no context: nothing to ask, and no library read.
  if (refs.length === 0 || !resolvesAgainst) return null;
  return <PlacedReferenceUsabilityLines refs={refs} resolvesAgainst={resolvesAgainst} />;
};

const PlacedReferenceUsabilityLines: React.FC<{ refs: string[]; resolvesAgainst: ResolveSurfaces }> = ({
  refs,
  resolvesAgainst,
}) => {
  const { data } = useExpressionLibrary();
  const lines = refs.flatMap((ref) => {
    const entry = (data ?? []).find((candidate) => candidate.id === ref);
    const unusable = entry ? describeUnusableHere(entry.expression, resolvesAgainst) : null;
    return entry && unusable
      ? [{ ref, text: `"${entry.name}" ${unusable.charAt(0).toLowerCase()}${unusable.slice(1)}` }]
      : [];
  });
  if (lines.length === 0) return null;
  return (
    <VStack align="stretch" gap={0.5}>
      {lines.map((line) => (
        <Text key={line.ref} fontSize="xs" color="warning.ink">
          {line.text}
        </Text>
      ))}
    </VStack>
  );
};

/** The same control over a row that addresses a shared expression — its declaration is the target's. */
const ReferencedValueTypeControl: React.FC<{
  leaf: MappingLeaf;
  reference: ReferenceExpression;
  onChange: (next: string) => void;
  capability: true | CapabilityRefused | null;
  destination: DestinationFieldType | null;
}> = ({ leaf, reference, onChange, capability, destination }) => {
  const { data } = useExpressionLibrary();
  const entries = data ?? [];
  const lookup = (ref: string) => entries.find((entry) => entry.id === ref)?.expression;
  const entry = entries.find((candidate) => candidate.id === reference.ref);
  return (
    <ValueTypeSelect
      value={leaf.asType}
      // Until the library answers, nothing is claimed about what the expression declares — the
      // first option reads `auto`, which is *nobody has said*, rather than a guess (`#1002`).
      beneath={sentType(undefined, reference, destination, data ? lookup : null)}
      from={entry?.name ?? null}
      onChange={onChange}
      capability={capability}
    />
  );
};

/**
 * **What the first option says** — what is sent when this row states no `Type` (`FD11` §5–§6, `FD13`).
 *
 * It states the wire, because the runtime reads the same answer ({@link sentType}). Where the slot
 * declares a type, that is what goes (*auto (number)*), converted from whatever the source yields. Where
 * it does not, a **declaration** is somebody's statement and names whose (*number · from Coppa*), a
 * **derivation** is ours (*auto (number)*), and nothing known is *auto*: the value goes as it resolved.
 * Choosing it is also how an override is reverted, so there is no separate revert: it is `X31`'s *give
 * back the layer's own* as an option rather than a button.
 */
function inheritedOptionLabel(inForce: ReturnType<typeof sentType> | null, from: string | null): string {
  if (!inForce || inForce.source === 'row') return 'auto';
  const label = EXPRESSION_YIELD_LABEL[inForce.type];
  if (inForce.source === 'declared') return `${label} · from ${from ?? 'the expression'}`;
  return `auto (${label})`;
}

const ValueTypeSelect: React.FC<{
  value: MappingLeaf['asType'];
  /**
   * What applies when this row states nothing — asked **without** the row's own answer, so that an
   * operator reading an override can still see what choosing the first option gives back.
   */
  beneath: ReturnType<typeof sentType>;
  from: string | null;
  onChange: (next: string) => void;
  capability: true | CapabilityRefused | null;
}> = ({ value, beneath, from, onChange, capability }) => {
  const refused = isRefused(capability) ? capability : null;
  return (
    // **The refusal sits under its own control, not beside it** (2026-09-03). Inline, the sentence
    // is long enough to push *Send structured values as* onto a line of its own, which is the row
    // Todd asked for these three to share. Under it, the reason is still on the surface — which is
    // what `X28b` requires — and still unmistakably attached to the control it is about.
    <VStack align="flex-start" gap={1} maxW="16rem">
      <HStack gap={2} align="center">
        <KnobLabel label="Type" help={MAPPING_HELP.sendAs} disabled={!!refused} />
        {/* The reason hangs on the select rather than under it (`#1276`), and the native `title`
            goes with the prose — one claim, one mechanism. */}
        <ControlHover text={refused?.reason ?? null}>
          <NativeSelect.Root size="xs" width="auto" disabled={!!refused}>
            <NativeSelect.Field
              /* **Rendered as declared where the wire cannot act on it** (Todd, 2026-09-23). A
                 refused control used to render blank, which says *nothing is declared* when
                 something is; the first option carries the declaration, greyed with the reason. */
              value={refused ? '' : (value ?? '')}
              onChange={(e) => onChange(e.target.value)}
              aria-label="Type"
              bg="bg.card"
              color="fg.default"
              fontFamily="mono"
            >
              <option value="">{inheritedOptionLabel(beneath, from)}</option>
              {EXPRESSION_YIELD_TYPES.map((t) => (
                <option key={t} value={t}>
                  {EXPRESSION_YIELD_LABEL[t]}
                </option>
              ))}
            </NativeSelect.Field>
            <NativeSelect.Indicator />
          </NativeSelect.Root>
        </ControlHover>
      </HStack>
    </VStack>
  );
};

/**
 * One of the two unconditional conditions, as a checkbox.
 *
 * The pair sits at one level because they are **one concept** — *not on the wire* — differing only
 * in when it holds, and because both are unconditional to author: a boolean set once, worth no
 * clicks to reach. `optional` keeps its name; it is already the author's word and already means one
 * thing to them (*"this doesn't have to be on the wire"*).
 */
/**
 * **How much this destination wants a value here** (`X46`).
 *
 * A checkbox rather than the three-member `FieldImportance` select, because only `required` has a
 * consequence an operator can act on and a control offering two answers nobody reads is noise.
 * `recommended` and `optional` are catalogue vocabulary, preserved on the row and rendered by the
 * badge; they are not something this control invents.
 *
 * **Unchecking stores nothing rather than `'optional'`**, so *nobody has stated one* keeps meaning
 * that — and a later catalogue correction can still reach the row.
 */
const ImportanceCheckbox: React.FC<{
  capability:
    | { value: FieldImportance | undefined; onChange: (next: FieldImportance | undefined) => void }
    | CapabilityRefused;
}> = ({ capability }) => {
  const refused = isRefused(capability);
  const value = refused ? undefined : capability.value;
  return (
    <HStack gap={2} align="center" flexWrap="wrap">
      {/* The teaching sentence went to the hover in #1179 and the refusal followed it in `#1276`:
          both are read by an operator who reached for this control, and only a warning about the
          **value** stays on the surface. */}
      <ControlHover text={refused ? capability.reason : null}>
        <Checkbox.Root
          checked={isRequiredHere(value)}
          disabled={refused}
          onCheckedChange={(e) =>
            !refused && capability.onChange(e.checked ? 'required' : value === 'required' ? undefined : value)
          }
          size="sm"
        >
          <Checkbox.HiddenInput aria-label="Required by this destination" />
          <Checkbox.Control borderColor="warning.solid" />
          <Checkbox.Label fontSize="sm">Required by this destination</Checkbox.Label>
        </Checkbox.Root>
      </ControlHover>
      <FieldHelpTooltip ariaLabel="About Required by this destination" content={MAPPING_HELP.importance} />
    </HStack>
  );
};

/** A fixed value the server handed back as the placeholder: a credential is stored behind it (`CR4`). */
function isStoredSecret(leaf: MappingLeaf): boolean {
  return leaf.kind === 'static' && leaf.staticValue === CONFIG_SECRET_PLACEHOLDER;
}

/**
 * **Whether this row's fixed value is a credential** (`CR2`, `#1371`). A checkbox beside *Required*,
 * because both are claims about the row rather than about where its value comes from.
 *
 * Two sentences stay on the surface, both about **this** value (`#1276`): with no key configured the
 * value is stored as written (`CR7` — said where the operator is typing, not in a boot log), and a row
 * that resolves has nothing stored to protect (`CR3`).
 */
const SecretCheckbox: React.FC<{
  capability: NonNullable<MappingSurfaceCapabilities['declaresSecret']>;
}> = ({ capability }) => {
  const refused = isRefused(capability);
  return (
    <HStack gap={2} align="center">
      <ControlHover text={refused ? capability.reason : null}>
        <Checkbox.Root
          checked={!refused && capability.value}
          disabled={refused}
          onCheckedChange={(e) => !refused && capability.onChange(e.checked === true)}
          size="sm"
        >
          <Checkbox.HiddenInput aria-label="Secret" />
          <Checkbox.Control />
          <Checkbox.Label fontSize="sm">Secret</Checkbox.Label>
        </Checkbox.Root>
      </ControlHover>
      <FieldHelpTooltip ariaLabel="About Secret" content={MAPPING_HELP.secret} />
    </HStack>
  );
};

/** What Secret has to say about **this** value, beneath the row of claims (`CR3`, `CR7`). */
const SecretNotes: React.FC<{
  capability: NonNullable<MappingSurfaceCapabilities['declaresSecret']>;
  leaf: MappingLeaf;
}> = ({ capability, leaf }) => {
  if (isRefused(capability) || !capability.value) return null;
  if (leaf.kind !== 'static') {
    return (
      <Text fontSize="xs" color="fg.muted" mt={1}>
        This value comes from somewhere when the request is sent, so nothing is stored to protect.
      </Text>
    );
  }
  if (capability.encryptedAtRest === false) {
    return (
      <Text fontSize="xs" color="warning.ink" mt={1}>
        Stored as written: this deployment has no encryption key, so anyone who can read the configuration database can
        read it.
      </Text>
    );
  }
  return null;
};

/**
 * **A stored credential: set, replace, or clear** (`CR4`). The console never reads the value back, so
 * the line says *set* rather than drawing dots — a fixed mask lies about a length, a proportional one
 * leaks it.
 *
 * **Clear is distinct from empty.** Replacing opens a box and the stored value stands until something
 * is typed in it, so an empty box can never erase a credential by accident; only *Clear* does.
 */
const StoredSecretLine: React.FC<{ leaf: MappingLeaf; onChange: (next: MappingLeaf) => void }> = ({
  leaf,
  onChange,
}) => {
  const [replacing, setReplacing] = React.useState(false);
  const [draft, setDraft] = React.useState('');
  const keepStored = () => onChange({ ...leaf, kind: 'static', staticValue: CONFIG_SECRET_PLACEHOLDER });
  if (replacing) {
    return (
      <HStack gap={2}>
        <Input
          size="sm"
          type="password"
          autoComplete="off"
          aria-label="New secret value"
          placeholder="Type the new value — the stored one stands until you do"
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            if (e.target.value === '') keepStored();
            else onChange({ ...leaf, kind: 'static', staticValue: e.target.value });
          }}
        />
        <Button
          size="xs"
          variant="ghost"
          onClick={() => {
            setReplacing(false);
            setDraft('');
            keepStored();
          }}
        >
          Keep stored
        </Button>
      </HStack>
    );
  }
  return (
    <HStack gap={2}>
      <Badge colorPalette="green" variant="subtle" aria-label="A secret value is stored">
        set
      </Badge>
      <Text fontSize="xs" color="fg.muted">
        Stored encrypted and not shown.
      </Text>
      <Button size="xs" variant="ghost" onClick={() => setReplacing(true)}>
        Replace
      </Button>
      <Button
        size="xs"
        variant="ghost"
        color="error.solid"
        onClick={() => onChange({ ...leaf, kind: 'static', staticValue: '' })}
      >
        Clear
      </Button>
    </HStack>
  );
};

/**
 * **Is this on the wire — one control with named answers** (`X29`, built 2026-09-02).
 *
 * It was two checkboxes, *Optional* and *Never send*, which an operator had to combine in their
 * head: neither said what the request would carry, and the pair could express `[always,
 * unresolved]`, a state the driver does not distinguish. `X29`: *"one control with named answers
 * makes one of them obviously the answer."*
 *
 * Three things the shape had to get right, each of them a correction to the first draft:
 *
 * - **The group is headed by its subject, not by one of its conditions** (`X46a`). *If it isn't
 *   there* was the draft heading, and *never send* is not conditional on absence — it is
 *   answerable before anything resolves, which is why `omittedUnconditionally` exists.
 * - **`optional` loses its name here** (`X46`). The word also named the destination's required-ness
 *   claim, which is now its own control in the zone above; the answers say the outcome instead.
 * - **A refused answer is a disabled option, not a shorter list.** Pattern 24, and it is the
 *   question this slice was parked on: the surfaces refuse the two conditions *separately*
 *   (`refuses.optional`, `refuses.neverSend`), so refusing one must not remove the other or
 *   collapse the control. An option that cannot be chosen **stays visible with its reason**, and a
 *   refused answer that is nonetheless the row's current state still renders as selected — on a
 *   header or an ORTB body key that is the honest reading: *this is what happens here, and you
 *   cannot change it.*
 */
/**
 * **While a layering row declares nothing, the two *send it* answers have no subject** (`X33`).
 *
 * Declared beside the select rather than inside it because the panel renders every omission
 * refusal in one place, and a sentence only the `<option disabled>` knew about would be invisible
 * to an operator who never opened the list — which is where a refusal is least likely to be read
 * as it is.
 *
 * The reason it is a refusal at all: *always send it* and *no override at all* store the **same
 * document** on a layering surface — an entry with no value slot — so offering the choice would be
 * a control that changes nothing (the `#1017` family), and picking it would snap straight back to
 * *fall back*, which reads as broken rather than as refused.
 *
 * **`never` stays enabled**, which is what keeps this from being the one-way door the rule about a
 * state's own control is written against: suppressing needs no value of its own. The other way out
 * is the value zone directly above, which is never disabled — and the sentence names it, because a
 * refusal is the sentence least likely to be checked.
 */
function declineRefusal(layering: { beneath: string; inheriting: boolean } | null): CapabilityRefused | undefined {
  if (!layering?.inheriting) return undefined;
  return {
    reason: `Give this row a value first — a round override with nothing of its own has nothing to send, and goes out as ${layering.beneath} either way.`,
  };
}

const OmissionSelect: React.FC<{
  capability:
    | {
        value: MappingOmission;
        onChange: (next: MappingOmission) => void;
        refuses?: { optional?: CapabilityRefused; neverSend?: CapabilityRefused };
        layering?: { beneath: string; onInherit: () => void; inheriting: boolean } | null;
      }
    | CapabilityRefused;
  whenUnresolved: UnresolvedBehaviour | null;
}> = ({ capability, whenUnresolved }) => {
  const wholeSurfaceRefused = isRefused(capability);
  const value = wholeSurfaceRefused ? ALWAYS_SENT : capability.value;
  /**
   * **The fourth answer, offered only where there is something beneath** (`X33`).
   *
   * It is *not offered and disabled* on the other surfaces, which is the `null` case pattern 24
   * already distinguishes from a refusal: a demand source has no layer under it, so the question
   * does not arise rather than being declined.
   */
  const layering = wholeSurfaceRefused ? null : (capability.layering ?? null);
  const answer = layering?.inheriting ? 'inherit' : omissionAnswerOf(value);
  /**
   * **`refuses.optional` refuses the *choice between* two answers — it does not refuse both.**
   *
   * `always` and `whenUnresolved` differ **only** in what happens when nothing resolves, so a
   * surface that cannot express that difference cannot let an operator pick between them. But it
   * still **is** one of them on every request, and that one has to stay selectable — otherwise
   * choosing *Never send it* is a one-way door, which is what shipped and what Todd reported:
   * *"cannot unset 'never send it' on ortb leaf editor & headers … send always and omit if doesn't
   * resolve are greyed out."*
   *
   * **Which one it is is not a special case — {@link MappingSurfaceCapabilities.whenUnresolved}
   * already declares it.** A body key or a header omits an unresolved value, so those surfaces
   * *are* `whenUnresolved` and cannot be `always`; a query parameter sends `foo=`, so it is
   * `always` — and it refuses nothing, because it can genuinely offer the choice.
   *
   * A surface that refuses the choice **and** declares no unresolved behaviour has told us nothing
   * about which of the two it is, so neither is disabled. Claiming there would be guessing, and
   * guessing wrong here is exactly the trap above.
   */
  const cannotChooseUnresolved = wholeSurfaceRefused ? undefined : capability.refuses?.optional;
  const surfaceAlwaysDoes: OmissionAnswer | null =
    whenUnresolved === null ? null : whenUnresolved === 'omit' ? 'whenUnresolved' : 'always';
  const refusesUnlessItIsWhatThisSurfaceDoes = (option: OmissionAnswer) =>
    cannotChooseUnresolved && surfaceAlwaysDoes !== null && surfaceAlwaysDoes !== option
      ? cannotChooseUnresolved
      : undefined;

  /**
   * **While the row declares nothing, the two *send it* answers have no subject** (`X33`).
   *
   * They are refused rather than left enabled, and the reason matters: *always send it* and *no
   * override at all* store the **same document** on a layering surface — an entry with no value
   * slot — so offering the choice would be a control that changes nothing, the `#1017` family.
   * Picking it would snap straight back to *fall back*, which reads as broken rather than as
   * refused.
   *
   * **`never` stays enabled**, and that is what keeps this from being the one-way door the rule
   * about a state's own control is written against: suppressing needs no value of its own. The
   * other way out is the value zone directly above, which is never disabled — and the refusal
   * names it, because a refusal is the sentence least likely to be checked.
   */
  const needsAValueFirst = declineRefusal(layering);

  const refusedFor: Record<OmissionAnswer, CapabilityRefused | undefined> = {
    always: wholeSurfaceRefused ? capability : (needsAValueFirst ?? refusesUnlessItIsWhatThisSurfaceDoes('always')),
    whenUnresolved: wholeSurfaceRefused
      ? capability
      : (needsAValueFirst ?? refusesUnlessItIsWhatThisSurfaceDoes('whenUnresolved')),
    never: wholeSurfaceRefused ? capability : capability.refuses?.neverSend,
    inherit: wholeSurfaceRefused ? capability : undefined,
  };

  /** The options this surface has, in the order the canvas puts them: decline first, then the wire. */
  const options: OmissionAnswer[] = layering
    ? ['inherit', 'always', 'whenUnresolved', 'never']
    : ['always', 'whenUnresolved', 'never'];
  const labelFor = (option: OmissionAnswer) =>
    option === 'inherit' && layering ? `Fall back to ${layering.beneath}` : OMISSION_ANSWER_LABEL[option];

  return (
    <Box>
      {/* **Label · control on one line, like every other knob on this panel** (`XS3` slice 3). It
          had its own stacked heading, which is what put it on a row of its own — and Todd, opening
          the slice: *"send as, send structured values belong together on a row, on the wire could
          also be on that same row."* They can, once the prose under each of them is in the hover. */}
      <HStack gap={2} align="center">
        <KnobLabel label="Is this on the wire" help={MAPPING_HELP.omission} disabled={wholeSurfaceRefused} />
        <NativeSelect.Root size="sm" width="fit-content" disabled={wholeSurfaceRefused}>
          <NativeSelect.Field
            aria-label="Is this on the wire"
            value={answer}
            onChange={(e) => {
              if (wholeSurfaceRefused) return;
              const next = omissionForAnswer(e.currentTarget.value as OmissionAnswer);
              // `null` is *this layer declares nothing*, which only the layering surface can store —
              // it owns the row that has to stop overriding. Routing it through `onChange` would
              // make every surface handle a case it has declared impossible.
              if (next === null) layering?.onInherit();
              else capability.onChange(next);
            }}
          >
            {options.map((option) => (
              // `disabled` rather than filtered out — and never on the option the row is already on,
              // or the select would render a value it does not offer.
              <option key={option} value={option} disabled={!!refusedFor[option] && option !== answer}>
                {labelFor(option)}
              </option>
            ))}
          </NativeSelect.Field>
          <NativeSelect.Indicator />
        </NativeSelect.Root>
      </HStack>
      {/* **What the selected answer actually does here.** The labels are outcomes and still cannot
          say which surface this is: *always send it* means `foo=` on a query parameter and means
          nothing at all on a body key. It stays **on the surface** rather than joining the hover
          (`X28b`): it changes with the selection, so it is a statement about this row's state and
          not teaching text. */}
      {answer === 'inherit' && layering ? (
        <Text fontSize="xs" color="fg.muted" mt={1} maxW="22rem">
          This round decides nothing here — {layering.beneath} goes out, exactly as on a round with no override.
        </Text>
      ) : (
        whenUnresolved && (
          // Bounded for the same reason a refusal is: this group shares a row with two others, and a
          // sentence allowed to run its natural width is what pushes the third one onto a line of its own.
          <Text fontSize="xs" color="fg.muted" mt={1} maxW="22rem">
            {describeOmissionAnswer(answer, whenUnresolved)}
          </Text>
        )
      )}
    </Box>
  );
};
/**
 * **The note — panel-level, and never dimmed** (Todd, 2026-09-03, `XS3` slice 3).
 *
 * It was the sixth capability chip, and Todd's report is the whole argument: *"when set to 'never
 * send it' the note control is also disabled… not sure it's the same 'kind' as the rest of the pills
 * in that row, other than it describes the expression."*
 *
 * Two properties fall straight out of that, and both were wrong while it was a chip:
 *
 * - **It is not a wire capability.** Every other chip changes what resolves; this changes nothing
 *   the driver ever sees. So it does not belong inside the region *never send* greys out — and a
 *   suppressed row is precisely the one an operator most wants to leave a note on, saying why.
 * - **It is not something to go looking for.** The other five are things the value *could* do, which
 *   is what makes a verb chip the right form. A note is documentation, and the point of it is that
 *   the next reader finds it without opening anything.
 *
 * **Where the model has nowhere to keep one, it is disabled with its reason** (pattern 24), rather
 * than removed or — as the chip did — stored by *replacing the operator's value with an empty path*.
 * A note lives on `CalculatedExpression.description`, so a fixed literal has no slot for it, and
 * inventing one would be a property that does not survive the save (`#1184`'s shape).
 */
const NoteControl: React.FC<{
  leaf: MappingLeaf;
  onChange: (next: MappingLeaf) => void;
}> = ({ leaf, onChange }) => {
  const calculated = leaf.expression?.type === 'calculated' ? leaf.expression : undefined;
  const other = leaf.expression && leaf.expression.type !== 'calculated' ? leaf.expression : undefined;
  const description = calculated?.description ?? other?.description;
  /**
   * **The reason names the case it is about** (#1045, caught rehearsing the ORTB body).
   *
   * One sentence covered both, and on a `{url}` leaf it read *"a fixed value has nowhere to keep
   * one"* — true of a literal and false of the row it was sitting on. A refusal is read by somebody
   * being turned away, which is the least-checked sentence in the UI, so it has to be true of the
   * value in front of them.
   */
  // A macro is an expression node since `X58`, so it has a home for a note like any other shape.
  const refused: CapabilityRefused | null = calculated
    ? null
    : other
      ? null
      : {
          reason:
            'A note travels with the expression that reads the value. This is a fixed value, so there is nowhere on the row to keep one.',
        };

  return (
    <HStack gap={2} align="center" mt={3} flexWrap="wrap">
      <KnobLabel label="Note" help={MAPPING_HELP.note} disabled={!!refused} />
      {/* `grow`, because the wrapper is now the flex child and this is the one control on the panel
          that fills its row rather than sizing to its content. */}
      <ControlHover text={refused?.reason ?? null} grow>
        <Input
          size="sm"
          bg="bg.card"
          width="100%"
          minW="14rem"
          aria-label="Note about this mapping"
          placeholder={refused ? 'no place to keep one on this value' : 'What this mapping is for'}
          disabled={!!refused}
          value={refused ? '' : (description ?? '')}
          onChange={(e) => {
            const next = e.target.value || undefined;
            if (calculated) onChange({ ...leaf, expression: { ...calculated, description: next } });
            else if (other) onChange({ ...leaf, expression: { ...other, description: next } });
          }}
        />
      </ControlHover>
    </HStack>
  );
};

export const MappingControl: React.FC<MappingControlProps> = ({
  leaf,
  onChange: onChangeProp,
  can,
  onSaveToLibrary,
  pathSuggestions,
  pathPlaceholder,
  declaredType,
  staticSuggestions,
}) => {
  const {
    acceptsMacro,
    acceptsDerived,
    offersLibrary,
    resolvesAgainst,
    storesValueType,
    surface,
    whenUnresolved,
    sendAs,
    storesMacroDefault,
    declaresOmission,
    declaresImportance,
    declaresSecret,
  } = can;

  /**
   * **One emitter, so a claim cannot be lost by a value edit.**
   *
   * Almost every handler below rebuilds the leaf from `{ path, kind, …slots }` rather than
   * spreading the old one, and that is deliberate: `N11f` says a row must not carry two answers,
   * so a change of kind has to *clear* the slots it is not using. What it must not clear is what the
   * leaf claims about itself — `optional`, `suppress`, `provenance` — and nine of the thirteen
   * call sites did, so changing the value of a required body leaf silently un-required it.
   *
   * Fixed here rather than at each site on purpose. Thirteen corrections would be right today and
   * wrong the moment a fourteenth handler is written, which is the shape of the defect rather than
   * an instance of it.
   */
  const onChange = React.useCallback(
    (next: MappingLeaf) => onChangeProp({ ...leafClaims(leaf), ...next }),
    [onChangeProp, leaf],
  );

  /**
   * **Never sent, so the mapping is kept but not used** — disabled, never collapsed or cleared
   * (Todd, 2026-08-27, asking what should happen to the editing panel).
   *
   * Collapsing would make one control do two things, and the second one moves every row below it
   * on the page — which an operator toggling *while testing* pays for on every toggle. Disabling
   * also reuses the language this control already speaks for a refused capability (`N11g`), rather
   * than teaching a second one for the same idea. And nothing is hidden, so *"unchecking restores
   * what you had"* is something the operator can see rather than a promise they have to trust.
   *
   * **What it covers is zone 2, the mapping, and nothing else** (2026-09-03). Two things sat inside
   * it that must not: the note, which is documentation rather than a wire capability
   * ({@link NoteControl}); and — caught by rehearsing this slice rather than by any gate — **the
   * omission select itself**, which is the control that got the row here and the only way back.
   * Putting it inside the region it governs makes *Never send it* a one-way door: the same defect
   * `X29` was corrected for on 2026-09-02, arriving a day later through layout rather than through a
   * disabled option. Zones 3 and 4 stay live for that reason.
   */
  const neverSent =
    declaresOmission !== null && !isRefused(declaresOmission) && isDeclared(declaresOmission.value, NEVER_SENT);

  /** Every refusal on the omission choice, deduplicated — a surface may refuse both for one reason. */
  const omissionReasons = declaresOmission
    ? [
        ...new Set(
          (isRefused(declaresOmission)
            ? [declaresOmission]
            : [
                declaresOmission.refuses?.optional,
                declaresOmission.refuses?.neverSend,
                // Dynamic, and it belongs here for the reason `declineRefusal` gives: the operator
                // who needs it is the one who has not opened the list.
                declineRefusal(declaresOmission.layering),
              ]
          )
            .filter(Boolean)
            .map((r) => (r as CapabilityRefused).reason),
        ),
      ]
    : [];

  /**
   * **The expression editor opens in the row's own expansion** (`X40`, `XS3` slice 3).
   *
   * It was the fourth tab, so reaching a condition meant leaving the value — `F7`, which #1223 is
   * about and which this **dissolves** rather than fixes: there is no longer anywhere else to be.
   * `If…` / `Calculate…` / `Shared…` reveal it; a shape the value line cannot show keeps it open,
   * because for a conditional there is nothing else to render.
   *
   * Slice 4 makes the handoff carry the typed value in and the result back out. This slice only has
   * to make sure the way up still exists after the strip is gone — *removing a picker is only an
   * improvement if what it advertised is still discoverable* (`X28`).
   *
   * **It is derived from the value's shape and holds no state of its own** (`X38`). There used to be
   * an `editorOpen` flag the errands set, which was already redundant — an errand *reshapes*, and
   * every shape it produces is one the value line cannot show — and after `X38` it was worse than
   * redundant: with the editor drawing nothing for a `static` or a `calculated`, a flag left true by
   * an errand the operator then cleared would have kept an empty container open under the row.
   */
  /**
   * **A set `Shared …` chip opens what it holds** (`X30a`, `#1290`, Todd 2026-09-10: *"click the
   * 'shared..' pill to see the read-only expression"*).
   *
   * It used to open the **picker**, which is what an *unset* chip does — so the one gesture that
   * every other set chip in this row gives to *show me this* was, on the only shape the row does not
   * own, *replace this*. The three acts are three controls: the chip opens it, the panel's *Use a
   * different one…* replaces it, and the chip's ✕ comes off it.
   *
   * **The state machine is `useSharedExpressionSlot`** (`X30c`, `#1303`) — it was built here and a
   * branch outcome and a calculation operand each kept their own copy of the older behaviour, so the
   * capability existed at the top of a value and nowhere inside it. Reported by Todd the same day.
   */
  const sharedSlot = useSharedExpressionSlot(leaf.kind === 'expression' ? leaf.expression : undefined);
  /* The surface's own two answers about the library, handed to the shared surfaces the way the
     expression editor hands them to a branch — one provider, so a row and the values inside it
     cannot disagree about whether this surface has a library at all. */
  const libraryAccess = React.useMemo(() => ({ offersLibrary, resolvesAgainst }), [offersLibrary, resolvesAgainst]);
  // What a typed literal is stored as: the destination's single-value type, or a list (`4f`, `#1419`).
  const literalType = declaredType === 'array' ? 'array' : (coercibleScalar(declaredType ?? undefined) ?? null);
  const listOnLine = showsListOnLine(leaf, literalType);
  const inlineValue = rendersOnValueLine(leaf) || listOnLine;
  const structuredStatic = leaf.kind === 'static' && isStructuredStatic(leaf.staticValue) && !listOnLine;
  /**
   * **A shared expression is edited in the library, not here** (`X5`, `XS5`, `#1066`).
   *
   * A reference is a shape the value line cannot show, so without this clause it would fall through
   * to the expression editor — which would draw an editor for a shape it cannot author, over a
   * value this row does not own. The row renders *from &lt;name&gt;* and what the expression does;
   * changing it is the library's act, because it reaches every row that addresses it.
   */
  const addressesSharedExpression = !!sharedSlot.reference;
  const showExpressionEditor =
    !sharedSlot.pickerOpen && !inlineValue && !structuredStatic && !addressesSharedExpression;

  /**
   * **The errand carries the row's value into the shape it names** (`X28`, `XS3` slice 4).
   *
   * Slice 3 made the three chips *reveal* the editor, which kept the way up discoverable and no more:
   * the operator had typed `session.content.contentLength`, asked for *send one value when a
   * condition holds*, and got a blank conditional to retype it into. `wrapForErrand` puts the value
   * in the branch, or in the first operand, and returns the **existing** expression untouched where
   * it is already that shape — clicking `If…` on a conditional is *show me it* (`X26`).
   *
   * `Shared…` carries nothing, because picking **replaces** the value.
   */
  const runErrand = (slot: CapabilitySlot) => {
    if (slot === 'shared') {
      // A **set** chip shows what it holds; an unset one offers the library — `useSharedExpressionSlot`
      // owns that rule, so the row, a branch and an operand cannot come to answer it differently.
      sharedSlot.activate();
      return;
    }
    if (slot === 'if' || slot === 'calculate') {
      const target = slot === 'if' ? 'conditional' : 'computed';
      // **A value that is already another expression shape is *reshaped*, not refused** (`#1229`,
      // Todd 2026-09-03: *"there's no way to change it except the drop down"*). Going from a
      // conditional to a calculation is one click on the chip, carrying what both shapes can hold —
      // the same rule the dropdown used, now on the control that replaces it.
      const current = leaf.kind === 'expression' ? leaf.expression : undefined;
      const wrapped =
        current && current.type !== 'calculated' && current.type !== 'static'
          ? reshapeExpression(current, target)
          : wrapForErrand(leaf, slot);
      if (!wrapped) return;
      if (wrapped !== leaf.expression) {
        onChange({ path: leaf.path, kind: 'expression', expression: wrapped, asType: leaf.asType });
      }
    }
  };

  /**
   * **What Extract and Transform would compose onto** — see `CapabilityChipRow.basePath`.
   *
   * A macro resolves to the path its concept declares (`X27`: a macro is sugar over a path), so
   * adding a transform to `{app_name}` produces the transform on `session.app.name` rather than on
   * an empty path — which is what it did, silently, until this slice. Where the vocabulary declares
   * no path there is nothing to compose onto and the two chips say so.
   */
  const basePath = compositionBasePath(leaf);
  /**
   * **A refusal names the case it is about** (#1045; four shapes, found one and two at a time).
   *
   * One sentence covered *literal* and *pathless macro*, which is every shape the value line can
   * render. Slice 4's errands put a `conditional` and a `computed` in the leaf and both then read
   * *"this is a fixed value"*, on the row that had just stopped being one. A refusal is read by
   * whoever is being turned away, so it is the least-checked sentence in the UI.
   *
   * **The expression shapes are answered by `chipCapabilitiesFor`**, which the expression editor
   * reads too — a chip refused on the row and the same chip refused inside the editor must not come
   * to say different things. The leaf answers only the two shapes an expression never has.
   */
  const shared = chipCapabilitiesFor(leaf.kind === 'expression' ? leaf.expression : undefined);

  /**
   * **Why the two errands cannot carry this value** — `null` where they can.
   *
   * Since `#1229` an expression that is already another shape is **reshaped** rather than refused, so
   * this is down to the two leaf shapes with no expression form at all.
   */
  const isMacro = leaf.expression?.type === 'macro';
  const refusesCarry: CapabilityRefused | null = isCarryable(leaf)
    ? null
    : isMacro
      ? {
          reason:
            'This macro declares no session path, so there is nothing to carry into a condition or a calculation.',
        }
      : leaf.kind === 'expression'
        ? null
        : { reason: 'A value edited as JSON has no single form to carry into a condition or a calculation.' };

  const refusesComposition: CapabilityRefused | null = isMacro
    ? basePath
      ? null
      : {
          reason:
            'Extract and Transform act on a value read from the session, and this macro declares no path to read.',
        }
    : leaf.kind === 'expression'
      ? shared.refusesComposition
      : basePath
        ? null
        : {
            reason:
              'Extract and Transform act on a value read from the session. This is a fixed value, so there is nothing to act on.',
          };

  const setAsType = (value: string) => {
    const asType = isExpressionYieldType(value) ? value : undefined;
    // **A literal takes its Type when the Type is chosen**, not at the next keystroke. Typing
    // converts through `coerceStaticValue`, so choosing *number* over `"1009"` stored `$type` and
    // left the text, and the raw JSON and the wire both said `"1009"` until the value was retyped.
    // Same function as typing, so a value that will not convert is kept as written.
    // With no Type the literal goes as the field's type (`FD13`), so it is stored that way too.
    const scalar = coercibleScalar(asType ?? declaredType ?? undefined);
    const literal = leaf.kind === 'static' ? leaf.staticValue : undefined;
    const converted =
      scalar && staticValueType(literal) ? { staticValue: coerceStaticValue(String(literal), scalar) } : {};
    onChange({ ...leaf, ...converted, asType });
  };

  return (
    <Box bg="bg.subtle" borderWidth="1px" borderColor="border.default" borderRadius="md" p={3}>
      {/* **Zone 1 · where to put it** (`X35`) — a claim about the *destination* rather than about
          the value, which is why it sits above the mapping and not beside the omission answers
          (`X46`). It changes no byte the driver sends.

          The row's own header already names the field, so this panel does **not** repeat it. It
          used to open *"Mapping for `<name>`"* directly under the header carrying that same name —
          reported by Todd 2026-09-03, and it is one name per concept applied to a screen: two
          renderings of one fact, the second of which costs a line of every panel. */}
      {/* **One row of claims about the row** (Todd, 2026-09-25): Required and Secret side by side, and
          what Secret needs to say about this value on the line beneath. */}
      {(declaresImportance || declaresSecret) && (
        <Box mb={3}>
          <HStack gap={5} align="center" flexWrap="wrap">
            {declaresImportance && <ImportanceCheckbox capability={declaresImportance} />}
            {declaresSecret && <SecretCheckbox capability={declaresSecret} />}
          </HStack>
          {declaresSecret && <SecretNotes capability={declaresSecret} leaf={leaf} />}
        </Box>
      )}
      {/* **Kept, and visibly not in use.** The mapping below is inert while *Never send* is
          selected, so it is dimmed and made unreachable rather than hidden — see `neverSent`. The
          sentence says the outcome rather than naming the control that caused it (#1045): a
          dimmed panel with no explanation reads as broken, which is the state disabling was
          introduced to end. */}
      {neverSent && (
        <Text fontSize="xs" color="fg.muted" mb={3} bg="bg.canvas" px={2} py={1} borderRadius="sm">
          Never sent. This mapping is kept, and is not used.
        </Text>
      )}
      <Box opacity={neverSent ? 0.45 : 1} pointerEvents={neverSent ? 'none' : undefined} aria-hidden={neverSent}>
        {/* **Zone 2 · where to get it.** One field, and the kind is a read-out (`X23`, `X24`). The
            Default shares the line, because *read this, send that if it is not there* is one answer
            rather than two settings (#1128). */}
        <HStack gap={2} mb={1}>
          <KnobLabel label="Where to get it" help={MAPPING_HELP.value} />
        </HStack>
        <VStack align="stretch" gap={2}>
          {declaresSecret && !isRefused(declaresSecret) && declaresSecret.value && isStoredSecret(leaf) ? (
            <StoredSecretLine leaf={leaf} onChange={onChange} />
          ) : (
            <ValueLine
              leaf={leaf}
              onChange={onChange}
              // A typed literal is stored as the type the destination declares (`#923`), a list
              // included since `4f` (`#1419`): typed comma-separated, stored as a list.
              literalType={literalType}
              pathSuggestions={pathSuggestions ?? null}
              placeholder={pathPlaceholder ?? null}
              acceptsMacro={acceptsMacro === true && !isRefused(acceptsDerived)}
              macroRefusal={isRefused(acceptsMacro) ? acceptsMacro.reason : null}
              derivedRefusal={isRefused(acceptsDerived) ? acceptsDerived.reason : null}
              trailing={
                <LeafDefaultControl
                  leaf={leaf}
                  onChange={onChange}
                  storesMacroDefault={storesMacroDefault}
                  acceptsSystemDefault={can.acceptsSystemDefault}
                />
              }
            />
          )}
          {/* A structured static — an object or a scalar array — has no single-field
              representation, so it keeps the editor that can author it. `rendersOnValueLine` is
              `X23`'s `isSimplePath` doing the only job it was left: deciding a rendering. */}
          {structuredStatic && !sharedSlot.pickerOpen && (
            <StaticValueEditor
              leaf={leaf}
              onChange={onChange}
              declaredType={declaredType}
              suggestions={staticSuggestions}
              // A *static* value's type is decided by what was typed, so the refusal has nothing
              // to say here — the editor simply does not offer the coercion.
              storesValueType={storesValueType === true}
            />
          )}

          {declaredType === 'array' && !sharedSlot.pickerOpen && (
            <ListConversionNotice leaf={leaf} onChange={onChange} />
          )}

          {/* **`X18` after placement** (`4e`). The picker marks an expression that cannot resolve
              here at the moment it is chosen; this says so for as long as it stays true, because the
              library can change underneath a row that already holds the address. */}
          {leaf.kind === 'expression' && (
            <PlacedReferenceUsability expression={leaf.expression} resolvesAgainst={resolvesAgainst} />
          )}

          {/* **The capability chip row** (`X25`, `XS3` slice 1). What this value already does,
              readable without opening anything, beside what it could do. */}
          <CapabilityChipRow
            expression={leaf.expression}
            onChange={(expr) =>
              onChange({ path: leaf.path, kind: 'expression', expression: expr, asType: leaf.asType })
            }
            basePath={basePath}
            refuses={
              isRefused(acceptsDerived)
                ? // Every chip builds a worked-out value, which this destination cannot hold (`#1580`).
                  {
                    if: acceptsDerived,
                    calculate: acceptsDerived,
                    shared: acceptsDerived,
                    tested: acceptsDerived,
                    extract: acceptsDerived,
                    transform: acceptsDerived,
                  }
                : {
                    ...(refusesComposition ? { extract: refusesComposition, transform: refusesComposition } : {}),
                    ...(refusesCarry ? { if: refusesCarry, calculate: refusesCarry } : {}),
                  }
            }
            onOpenInEditor={runErrand}
            /**
             * **Which chip the surface below belongs to** — reported by Todd 2026-09-03: clicking
             * `Shared…` opened the picker and lit nothing, so the *set* `Calculate…` chip stayed the
             * only highlighted thing on the row and read as *that is what you clicked*.
             *
             * The picker wins over the shape, and that is the ordering that matters: while it is
             * open the value is about to be **replaced**, so the row's subject is the picker rather
             * than the shape the value happens to have.
             */
            openSlot={sharedSlot.openSlot ?? (showExpressionEditor ? openSlotForShape(leaf.expression) : null)}
          />

          {/* **The two surfaces the `Shared…` chip opens** (`X28a`, `X30a`) — rendered under the chip
              row rather than in a dialog, because both are about the value that is on screen above
              them. While the picker is open it is the **only** thing under the chip row (`#1229`,
              Todd 2026-09-03): picking replaces the value, so an editor for the value being replaced
              is a control whose every keystroke is about to be discarded. The value line stays,
              because it is what the choice is *about*; what is suspended is the editing of it. */}
          <ExpressionLibraryAccessContext.Provider value={libraryAccess}>
            {/* Another expression's editor, whose findings are its author's — see `OutsideExpressionLocations`. */}
            <OutsideExpressionLocations>
              <SharedExpressionSurfaces
                slot={sharedSlot}
                onChange={(expr) =>
                  onChange({ path: leaf.path, kind: 'expression', expression: expr, asType: leaf.asType })
                }
                getFieldSuggestions={pathSuggestions ?? undefined}
              />
            </OutsideExpressionLocations>
          </ExpressionLibraryAccessContext.Provider>

          {showExpressionEditor && (
            <DecoratorExpressionEditor
              // The row's own `Type` knob is the override; the declaration is the shared editor's.
              authorsYield={null}
              emptyValue={can.emptyValue ? { outcome: can.emptyValue, name: leaf.path.join('.') } : null}
              expression={leaf.expression ?? defaultCalculatedExpression()}
              onChange={(expr) =>
                onChange({ path: leaf.path, kind: 'expression', expression: expr, asType: leaf.asType })
              }
              onSaveToLibrary={onSaveToLibrary}
              /* **Handed down, never re-derived** (`E4`, `#1272`). A branch inside this editor may
                 address a shared expression, and *may this surface use the library* is a question this
                 control already has the row's answer to. Deriving a second one here is a second place
                 for it to drift, and the two would disagree on the surface where one was forgotten. */
              offersLibrary={offersLibrary}
              resolvesAgainst={resolvesAgainst}
              /* The value line's own chip row is directly above this, and it is the shape control
                 (`X39`). A second one here would be two controls for one decision. */
              shapeControl="above"
              /* A *defaulting* source rather than an affordance: absent renders the outbound schema,
                 which is a correct answer. The required-and-nullable rule guards props that render
                 **nothing** when forgotten, and it is enforced at `MappingControlProps` — the
                 surface contract — not on the components inside it. */
              getFieldSuggestions={pathSuggestions ?? undefined}
            />
          )}
        </VStack>
      </Box>

      {/* **Zones 3 and 4, on one row** (Todd, 2026-09-03: *"send as, send structured values belong
          together on a row, on the wire could also be on that same row"*). They are three answers
          about **what leaves this row**, and they only fitted on one line once the standing prose
          under each of them moved into its `?` (#1179). The row wraps rather than compressing,
          because `join` turns one control into four.

          **In the order of their subject** (Todd, 2026-09-23, `FD11` §7a): *"`type` is the primary
          piece of information, `on the wire` and `send structured` are transform questions on the
          `type`."* So the fact comes first and the two transforms on it follow. It refines the
          2026-09-03 ruling, which was about grouping and said nothing about sequence. */}
      <HStack gap={4} align="flex-start" flexWrap="wrap" mt={3}>
        {/* **Not offered for a structured static**, where it can never apply: a value authored in
            the JSON editor is a scalar by construction nowhere in it, so the coercion has no case
            to act on and the knobs would sit beside a JSON box deciding nothing. */}
        {!structuredStatic && (
          <ValueTypeControl leaf={leaf} onChange={setAsType} capability={storesValueType} destination={declaredType} />
        )}

        {sendAs &&
          !structuredStatic &&
          (isRefused(sendAs) ? (
            // Disabled rather than absent: the operator has met this control on the parameter
            // rows, and a surface that simply does not show it cannot be told apart from one
            // where it is broken or hidden somewhere else.
            <ControlHover text={sendAs.reason}>
              <Box opacity={0.55}>
                <SendAsControl value={undefined} onChange={() => undefined} surface={surface} sends={null} disabled />
              </Box>
            </ControlHover>
          ) : (
            <SendAsControl value={sendAs.value} onChange={sendAs.onChange} surface={surface} sends={sendAs.sends} />
          ))}
        {declaresOmission && (
          /* On the select rather than on the option, because a native `<option>` carries no
             explanation and a disabled one with no reason is the silence pattern 24 exists to end.
             It was standing prose under the select until `#1276`, and this is the site the sweep
             pays for most: **up to three** sentences could stack there, which is `#1248`'s claim
             repeated where it does not vary. They join into the one hover an operator reaches by
             going for the control they cannot move. */
          <ControlHover text={omissionReasons.length > 0 ? omissionReasons.join(' ') : null}>
            <Box>
              <OmissionSelect capability={declaresOmission} whenUnresolved={whenUnresolved} />
            </Box>
          </ControlHover>
        )}
      </HStack>

      {/* **The note sits outside the dimmed region**, because it is documentation rather than a wire
          capability — and a suppressed row is the one most worth explaining. */}
      <NoteControl leaf={leaf} onChange={onChange} />
    </Box>
  );
};
